import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import { jwtDecode } from 'jwt-decode';
import { motion, AnimatePresence } from 'framer-motion';
import ParticlesBackground from '../../components/common/ParticlesBackground';
import {
  FaDatabase, FaCheckCircle, FaBuilding, FaChartBar,
  FaArrowRight, FaUserPlus, FaSignInAlt, FaMapMarkedAlt,
  FaLayerGroup, FaGlobeAsia, FaTachometerAlt, FaUser,
  FaClipboardList, FaChevronDown, FaChevronUp, FaSearch,
  FaChevronLeft, FaChevronRight, FaSignOutAlt, FaEye, FaTimes
} from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie } from 'recharts';
import '../../styles/landing.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [igtDetails, setIgtDetails] = useState(null);
  const [showIgtDetail, setShowIgtDetail] = useState(false);
  const [showKlDetail, setShowKlDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermKl, setSearchTermKl] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageKl, setCurrentPageKl] = useState(1);
  const itemsPerPage = 10;
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showStatusDetail, setShowStatusDetail] = useState(false);
  const [searchTermStatus, setSearchTermStatus] = useState('');
  const [currentPageStatus, setCurrentPageStatus] = useState(1);
  const [selectedIgtDetail, setSelectedIgtDetail] = useState(null);
  const [showIgtModal, setShowIgtModal] = useState(false);
  const [expandedChecklists, setExpandedChecklists] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection for header animation
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp > currentTime) {
          setUser({
            username: decoded.username,
            role: decoded.role,
            kl: decoded.kl,
            profileImage: decoded.profileImage
          });
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Token decode error:', error);
        localStorage.removeItem('token');
      }
    }

    const fetchStats = async () => {
      try {
        const res = await axios.get(`${config.API_URL}/api/management/public/stats`);
        if (res.data.success) {
          setStats(res.data.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchIGTDetails = async () => {
      try {
        const res = await axios.get(`${config.API_URL}/api/management/public/igt-details`);
        if (res.data.success) {
          setIgtDetails(res.data.data);
        }
      } catch (error) {
        console.error('Error fetching IGT details:', error);
      }
    };

    fetchStats();
    fetchIGTDetails();
  }, []);

  // Chart colors
  const CHART_COLORS = {
    'Renaksi': '#f59e0b',
    'Walidata': '#3b82f6',
    'Renaksi dan Walidata': '#10b981'
  };

  // Status IGT colors for pie chart
  const STATUS_COLORS = {
    'Sudah Terintegrasi': '#10b981',
    'Proses Verifikasi': '#f59e0b',
    'Belum Pengajuan': '#94a3b8'
  };

  // Get dashboard path based on role
  const getDashboardPath = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 0:
      case 2:
        return '/admin';
      case 1:
        return '/kl';
      case 3:
        return '/publikator';
      default:
        return '/';
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setShowUserDropdown(false);
    navigate('/');
  };

  // Animated counter component
  const AnimatedCounter = ({ value, duration = 2000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (value === undefined || value === null) return;

      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }, [value, duration]);

    return <span>{count.toLocaleString('id-ID')}</span>;
  };

  return (
    <div className="lp-page">
      <ParticlesBackground />

      {/* Navigation */}
      <nav className={`lp-nav ${isScrolled ? 'lp-nav-scrolled' : ''}`}>
        <Link to="/" className="lp-nav-brand">
          <img src="/logo.png" alt="KSP Logo" />
          <span>Integrasi IGT</span>
        </Link>
        <div className="lp-nav-links">
          {user ? (
            <>
             {/* <Link to="/public/metadata-editor" className="lp-nav-link">
                <FaLayerGroup /> Metadata Editor
              </Link> */}
              <div className="lp-user-dropdown-container">
                <div
                  className="lp-user-info lp-clickable"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  {user.profileImage ? (
                    <img src={user.profileImage} alt="Profile" className="lp-user-avatar" />
                  ) : (
                    <div className="lp-user-avatar-placeholder">
                      <FaUser />
                    </div>
                  )}
                  <span className="lp-user-name">{user.username}</span>
                  <FaChevronDown className={`lp-dropdown-arrow ${showUserDropdown ? 'open' : ''}`} />
                </div>
                {showUserDropdown && (
                  <div className="lp-user-dropdown">
                    <Link to={getDashboardPath()} className="lp-dropdown-item">
                      <FaTachometerAlt /> Dashboard
                    </Link>
                    <button onClick={handleLogout} className="lp-dropdown-item lp-logout-btn">
                      <FaSignOutAlt /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
             {/* <Link to="/public/metadata-editor" className="lp-nav-link">
                <FaLayerGroup /> Metadata Editor
              </Link> */}
              <Link to="/login" className="lp-nav-link">
                <FaSignInAlt /> Masuk
              </Link>
              <Link to="/register" className="lp-nav-link lp-btn-register">
                <FaUserPlus /> Daftar
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="lp-hero-section">
        <motion.div
          className="lp-hero-content lp-hero-centered"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>
            Aplikasi Integrasi
          </h1>
          <h1><span className="lp-gradient-text">Informasi Geospasial Tematik</span></h1>
          <p className="lp-hero-subtitle">
            Sistem pengelolaan dan verifikasi data Informasi Geospasial Tematik (IGT)
            Kementerian/Lembaga Indonesia
          </p>
          <div className="lp-hero-buttons">
            {user ? (
              <Link to={getDashboardPath()} className="lp-btn-primary">
                <FaTachometerAlt /> Buka Dashboard
              </Link>
            ) : (
              <button 
                className="lp-btn-primary"
                onClick={() => {
                  document.getElementById('stats-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <FaChartBar /> Lihat Data
              </button>
            )}
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section id="stats-section" className="lp-stats-section">
        <motion.div
          className="lp-stats-container"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="lp-section-title">
            <FaChartBar /> Statistik Data
          </h2>

          {loading ? (
            <div className="lp-stats-loading">
              <div className="lp-spinner"></div>
              <p>Memuat data...</p>
            </div>
          ) : stats ? (
            <div className="lp-stats-grid lp-stats-grid-3">
              {/* Total IGT Card */}
              <div
                className={`lp-stat-card lp-clickable ${showIgtDetail ? 'lp-active' : ''}`}
                onClick={() => { setShowIgtDetail(!showIgtDetail); setShowKlDetail(false); setShowStatusDetail(false); }}
                title="Klik untuk melihat detail"
              >
                <div className="lp-stat-icon">
                  <FaDatabase />
                </div>
                <div className="lp-stat-value">
                  <AnimatedCounter value={stats.total_igt} />
                </div>
                <div className="lp-stat-label">Total Data IGT</div>
                <div className="lp-stat-click-hint">
                  {showIgtDetail ? 'Klik untuk tutup' : 'Klik untuk detail'}
                </div>
                <div className="lp-stat-expand-icon">
                  {showIgtDetail ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>

              {/* Status IGT Card with Mini Pie */}
              <div
                className={`lp-stat-card lp-stat-card-status lp-clickable ${showStatusDetail ? 'lp-active' : ''}`}
                onClick={() => { setShowStatusDetail(!showStatusDetail); setShowIgtDetail(false); setShowKlDetail(false); }}
                title="Klik untuk melihat detail"
              >
                <div className="lp-stat-card-status-header">
                  <div className="lp-stat-header-text">
                    <div className="lp-stat-label">Status IGT</div>
                  </div>
                </div>
                {stats.status_igt_breakdown && stats.status_igt_breakdown.length > 0 && (
                  <div className="lp-status-pie-container">
                    <div className="lp-mini-pie-chart">
                      <ResponsiveContainer width="100%" height={100}>
                        <PieChart>
                          <Pie
                            data={stats.status_igt_breakdown.map(item => ({
                              name: item.status,
                              value: parseInt(item.count)
                            }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={25}
                            outerRadius={45}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {stats.status_igt_breakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || '#6b7280'} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ background: '#fff', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '11px' }}
                            formatter={(value) => [`${value}`, '']}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="lp-status-legend-mini">
                      {stats.status_igt_breakdown.map((item, index) => (
                        <div key={index} className="lp-status-legend-item">
                          <span className="lp-status-dot" style={{ background: STATUS_COLORS[item.status] || '#6b7280' }}></span>
                          <span className="lp-status-name">{item.status}</span>
                          <span className="lp-status-count">{parseInt(item.count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="lp-stat-click-hint2">
                  {showStatusDetail ? 'Klik untuk tutup' : 'Klik untuk detail'}
                </div>
                <div className="lp-stat-expand-icon">
                  {showStatusDetail ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>

              {/* K/L Card */}
              <div
                className={`lp-stat-card lp-clickable ${showKlDetail ? 'lp-active' : ''}`}
                onClick={() => { setShowKlDetail(!showKlDetail); setShowIgtDetail(false); setShowStatusDetail(false); }}
                title="Klik untuk melihat detail"
              >
                <div className="lp-stat-icon lp-kl">
                  <FaBuilding />
                </div>
                <div className="lp-stat-value">
                  <AnimatedCounter value={stats.total_kl} />
                </div>
                <div className="lp-stat-label">Kementerian/Lembaga</div>
                <div className="lp-stat-click-hint">
                  {showKlDetail ? 'Klik untuk tutup' : 'Klik untuk detail'}
                </div>
                <div className="lp-stat-expand-icon">
                  {showKlDetail ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>
            </div>
          ) : (
            <p className="lp-stats-error">Gagal memuat statistik</p>
          )}

          {/* Collapsible IGT Detail Section */}
          <AnimatePresence>
            {showIgtDetail && stats && (
              <motion.div
                className="lp-detail-panel"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Kelompok IGT Bar Chart */}
                {stats.kelompok_breakdown && stats.kelompok_breakdown.length > 0 && (
                  <div className="lp-kelompok-section">
                    <h3><FaClipboardList /> Chart Data IGT</h3>
                    <div className="lp-kelompok-chart-container">
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart
                          data={stats.kelompok_breakdown.map(item => ({
                            name: item.jenis || 'Lainnya',
                            shortName: item.jenis === 'Renaksi dan Walidata' ? 'Renaksi & Walidata' : (item.jenis || 'Lainnya'),
                            value: parseInt(item.count),
                            color: CHART_COLORS[item.jenis] || '#6b7280'
                          }))}
                          layout="vertical"
                          margin={{ top: 5, right: 50, left: 10, bottom: 5 }}
                        >
                          <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.2)' }} />
                          <YAxis type="category" dataKey="shortName" width={120} tick={{ fill: 'rgba(255,255,255,0.9)', fontSize: 10.5 }} axisLine={false} tickLine={false} />
                          <Tooltip
                            contentStyle={{ background: '#535557ff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', padding: '10px 14px' }}
                            labelStyle={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}
                            formatter={(value) => [`${value.toLocaleString('id-ID')} IGT`, 'Jumlah']}
                          />
                          <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={24} label={{ position: 'right', fill: '#fff', fontSize: 11, fontWeight: 600 }}>
                            {stats.kelompok_breakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[entry.jenis] || '#6b7280'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="lp-kelompok-legend">
                        {stats.kelompok_breakdown.map((item, index) => (
                          <div key={index} className="lp-legend-item">
                            <span
                              className="lp-legend-color"
                              style={{ background: CHART_COLORS[item.jenis] || '#6b7280' }}
                            ></span>
                            <span className="lp-legend-label">{item.jenis}: </span>
                            <span className="lp-legend-value">{parseInt(item.count).toLocaleString('id-ID')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Data IGT List with comprehensive columns */}
                {igtDetails && igtDetails.igt_data_list && igtDetails.igt_data_list.length > 0 && (() => {
                  // Filter data based on search
                  const filteredList = igtDetails.igt_data_list.filter(item => {
                    const search = searchTerm.toLowerCase();
                    return (
                      (item.kl && item.kl.toLowerCase().includes(search)) ||
                      (item.nama_igt && item.nama_igt.toLowerCase().includes(search)) ||
                      (item.kelompok_igt && item.kelompok_igt.toLowerCase().includes(search)) ||
                      (item.status_display && item.status_display.toLowerCase().includes(search)) ||
                      (item.jenis_igt && item.jenis_igt.toLowerCase().includes(search))
                    );
                  });

                  // Pagination
                  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const paginatedList = filteredList.slice(startIndex, startIndex + itemsPerPage);

                  // Format date helper
                  const formatDate = (dateStr) => {
                    if (!dateStr || dateStr === '1900-01-01T00:00:00.000Z') return '-';
                    const date = new Date(dateStr);
                    if (isNaN(date.getTime())) return '-';
                    return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
                  };

                  return (
                    <div className="lp-igt-list-section">
                      <div className="lp-igt-list-header">
                        <h3><FaDatabase /> Daftar Data IGT</h3>
                        <div className="lp-igt-search-box">
                          <FaSearch className="lp-search-icon" />
                          <input
                            type="text"
                            placeholder="Cari K/L, nama IGT, status..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="lp-search-input"
                          />
                        </div>
                      </div>
                      <div className="lp-igt-table-wrapper lp-wide-table">
                        <table className="lp-igt-table">
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}>No</th>
                              <th>Nama IGT</th>
                              <th>K/L</th>
                              <th style={{ width: '140px', textAlign: 'center' }}>Status</th>
                              <th style={{ width: '100px' }}>Jenis KSP</th>
                              <th style={{ width: '130px' }}>IGT Renaksi\Walidata</th>
                              <th style={{ width: '100px', textAlign: 'center' }}>Capaian</th>
                              <th style={{ width: '100px' }}>Tanggal</th>
                              <th style={{ width: '70px', textAlign: 'center' }}>Lihat Detail</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedList.length > 0 ? (
                              paginatedList.map((item, index) => (
                                <tr key={index}>
                                  <td>{startIndex + index + 1}</td>
                                  <td>{item.nama_igt || '-'}</td>
                                  <td>{item.kl || '-'}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <span
                                      className={`lp-status-badge ${item.status_display === 'Sudah Terintegrasi' ? 'lp-integrated' :
                                          item.status_display === 'Proses Verifikasi' ? 'lp-verification' : 'lp-pending'
                                        }`}
                                    >
                                      {item.status_display || 'Belum Pengajuan'}
                                    </span>
                                  </td>
                                  <td>{item.jenis_igt || '-'}</td>
                                  <td>
                                    {item.kelompok_igt ? (
                                      <span
                                        className={`lp-kelompok-badge ${item.kelompok_igt === 'Renaksi' ? 'lp-renaksi' :
                                            item.kelompok_igt === 'Walidata' ? 'lp-walidata' : 'lp-both'
                                          }`}
                                      >
                                        {item.kelompok_igt}
                                      </span>
                                    ) : (
                                      <span className="lp-kelompok-badge lp-other">-</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {(() => {
                                      // Parse capaian_data to get latest year's capaian
                                      if (item.capaian_data) {
                                        try {
                                          const capaianList = typeof item.capaian_data === 'string'
                                            ? JSON.parse(item.capaian_data)
                                            : item.capaian_data;
                                          if (Array.isArray(capaianList) && capaianList.length > 0) {
                                            // Get the latest year's capaian
                                            const latest = capaianList.reduce((a, b) => (a.tahun > b.tahun ? a : b));
                                            return `${latest.nilai} ${latest.satuan || ''}`.trim();
                                          }
                                        } catch (e) {
                                          console.error('Error parsing capaian_data in table:', e);
                                        }
                                      }
                                      return '-';
                                    })()}
                                  </td>
                                  <td>{formatDate(item.last_updated)}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      className="lp-action-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedIgtDetail(item);
                                        setShowIgtModal(true);
                                      }}
                                      title="Lihat Detail"
                                    >
                                      <FaEye />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="lp-no-data">Tidak ada data ditemukan</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination */}
                      {filteredList.length > itemsPerPage && (
                        <div className="lp-igt-pagination">
                          <span className="lp-pagination-info">
                            Menampilkan {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredList.length)} dari {filteredList.length} data
                          </span>
                          <div className="lp-pagination-controls">
                            <button
                              className="lp-pagination-btn"
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                            >
                              Awal
                            </button>
                            <button
                              className="lp-pagination-btn"
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                            >
                              <FaChevronLeft />
                            </button>
                            <span className="lp-pagination-page">
                              {currentPage} / {totalPages}
                            </span>
                            <button
                              className="lp-pagination-btn"
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                            >
                              <FaChevronRight />
                            </button>
                            <button
                              className="lp-pagination-btn"
                              onClick={() => setCurrentPage(totalPages)}
                              disabled={currentPage === totalPages}
                            >
                              Akhir
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Collapsible K/L Detail Section */}
          <AnimatePresence>
            {showKlDetail && stats && stats.kl_breakdown && (
              <motion.div
                className="lp-detail-panel"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* K/L Bar Chart with Search */}
                {(() => {
                  const filteredKlData = stats.kl_breakdown.filter(item => {
                    const search = searchTermKl.toLowerCase();
                    return item.nama_kl && item.nama_kl.toLowerCase().includes(search);
                  });

                  return (
                    <div className="lp-kelompok-section">
                      <div className="lp-igt-list-header">
                        <h3><FaBuilding /> Chart Jumlah IGT per K/L</h3>
                        <div className="lp-igt-search-box">
                          <FaSearch className="lp-search-icon" />
                          <input
                            type="text"
                            placeholder="Cari K/L..."
                            value={searchTermKl}
                            onChange={(e) => setSearchTermKl(e.target.value)}
                            className="lp-search-input"
                          />
                        </div>
                      </div>
                      <div className="lp-kl-chart-scroll-container">
                        <ResponsiveContainer width="100%" height={Math.max(filteredKlData.length * 32, 200)}>
                          <BarChart
                            data={filteredKlData.map(item => ({
                              name: item.nama_kl && item.nama_kl.length > 35 ? item.nama_kl.substring(0, 35) + '...' : item.nama_kl,
                              fullName: item.nama_kl,
                              value: parseInt(item.jumlah_igt)
                            }))}
                            layout="vertical"
                            margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                          >
                            <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.2)' }} />
                            <YAxis type="category" dataKey="name" width={240} tick={{ fill: 'rgba(255,255,255,0.9)', fontSize: 9.5 }} axisLine={false} tickLine={false} />
                            <Tooltip
                              contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', padding: '10px 14px', maxWidth: '300px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                              labelFormatter={(label, payload) => payload && payload[0] ? payload[0].payload.fullName : label}
                              labelStyle={{ color: '#fff', fontWeight: 600, marginBottom: 6, display: 'block', lineHeight: 1.3 }}
                              formatter={(value) => [`${value} IGT`, 'Jumlah']}
                              wrapperStyle={{ zIndex: 1000 }}
                            />
                            <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22} fill="#667eea" label={{ position: 'right', fill: '#fff', fontSize: 10, fontWeight: 600 }} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {filteredKlData.length === 0 && (
                        <div className="lp-no-chart-data">Tidak ada K/L ditemukan</div>
                      )}
                      <div className="lp-chart-info">
                        <span>Total: {filteredKlData.length} K/L</span>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Collapsible Status IGT Detail Section */}
          <AnimatePresence>
            {showStatusDetail && igtDetails && igtDetails.igt_status_list && (
              <motion.div
                className="lp-detail-panel"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {(() => {
                  const filteredStatusList = igtDetails.igt_status_list.filter(item => {
                    const search = searchTermStatus.toLowerCase();
                    return (
                      (item.nama_kl && item.nama_kl.toLowerCase().includes(search)) ||
                      (item.nama_igt && item.nama_igt.toLowerCase().includes(search)) ||
                      (item.status && item.status.toLowerCase().includes(search))
                    );
                  });

                  const totalPagesStatus = Math.ceil(filteredStatusList.length / itemsPerPage);
                  const startIndexStatus = (currentPageStatus - 1) * itemsPerPage;
                  const paginatedStatusList = filteredStatusList.slice(startIndexStatus, startIndexStatus + itemsPerPage);

                  return (
                    <div className="lp-igt-list-section">
                      <div className="lp-igt-list-header">
                        <h3><FaClipboardList /> Daftar Status IGT</h3>
                        <div className="lp-igt-search-box">
                          <FaSearch className="lp-search-icon" />
                          <input
                            type="text"
                            placeholder="Cari K/L, IGT, atau Status..."
                            value={searchTermStatus}
                            onChange={(e) => {
                              setSearchTermStatus(e.target.value);
                              setCurrentPageStatus(1);
                            }}
                            className="lp-search-input"
                          />
                        </div>
                      </div>
                      <div className="lp-igt-table-wrapper">
                        <table className="lp-igt-table">
                          <thead>
                            <tr>
                              <th style={{ width: '60px' }}>No</th>
                              <th>Kementerian/Lembaga</th>
                              <th>Nama IGT</th>
                              <th style={{ width: '160px', textAlign: 'center' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedStatusList.length > 0 ? (
                              paginatedStatusList.map((item, index) => (
                                <tr key={index}>
                                  <td>{startIndexStatus + index + 1}</td>
                                  <td>{item.nama_kl || '-'}</td>
                                  <td>{item.nama_igt || '-'}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <span
                                      className={`lp-status-badge ${item.status === 'Sudah Terintegrasi' ? 'lp-integrated' :
                                          item.status === 'Proses Verifikasi' ? 'lp-verification' : 'lp-pending'
                                        }`}
                                    >
                                      {item.status}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" className="lp-no-data">Tidak ada data ditemukan</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {filteredStatusList.length > itemsPerPage && (
                        <div className="lp-igt-pagination">
                          <span className="lp-pagination-info">
                            Menampilkan {startIndexStatus + 1}-{Math.min(startIndexStatus + itemsPerPage, filteredStatusList.length)} dari {filteredStatusList.length} IGT
                          </span>
                          <div className="lp-pagination-controls">
                            <button className="lp-pagination-btn" onClick={() => setCurrentPageStatus(1)} disabled={currentPageStatus === 1}>Awal</button>
                            <button className="lp-pagination-btn" onClick={() => setCurrentPageStatus(prev => Math.max(prev - 1, 1))} disabled={currentPageStatus === 1}><FaChevronLeft /></button>
                            <span className="lp-pagination-page">{currentPageStatus} / {totalPagesStatus}</span>
                            <button className="lp-pagination-btn" onClick={() => setCurrentPageStatus(prev => Math.min(prev + 1, totalPagesStatus))} disabled={currentPageStatus === totalPagesStatus}><FaChevronRight /></button>
                            <button className="lp-pagination-btn" onClick={() => setCurrentPageStatus(totalPagesStatus)} disabled={currentPageStatus === totalPagesStatus}>Akhir</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* IGT Detail Modal */}
      <AnimatePresence>
        {showIgtModal && selectedIgtDetail && (
          <motion.div
            className="lp-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowIgtModal(false)}
          >
            <motion.div
              className="lp-modal-content"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lp-modal-header">
                <h3>Detail IGT</h3>
                <button className="lp-modal-close" onClick={() => setShowIgtModal(false)}>
                  <FaTimes />
                </button>
              </div>
              <div className="lp-modal-body">
                {/* Basic Info */}
                <div className="lp-modal-section">
                  <h4>Informasi Dasar</h4>
                  <div className="lp-modal-info-grid">
                    <div className="lp-modal-info-item">
                      <span className="lp-modal-label">Nama IGT</span>
                      <span className="lp-modal-value">{selectedIgtDetail.nama_igt || '-'}</span>
                    </div>
                    <div className="lp-modal-info-item">
                      <span className="lp-modal-label">K/L</span>
                      <span className="lp-modal-value">{selectedIgtDetail.kl || '-'}</span>
                    </div>
                    <div className="lp-modal-info-item">
                      <span className="lp-modal-label">Status</span>
                      <span className={`lp-status-badge ${selectedIgtDetail.status_display === 'Sudah Terintegrasi' ? 'lp-integrated' :
                          selectedIgtDetail.status_display === 'Proses Verifikasi' ? 'lp-verification' : 'lp-pending'
                        }`}>
                        {selectedIgtDetail.status_display || 'Belum Pengajuan'}
                      </span>
                    </div>
                    <div className="lp-modal-info-item">
                      <span className="lp-modal-label">IGT Renaksi\Walidata</span>
                      <span className={`lp-kelompok-badge ${selectedIgtDetail.kelompok_igt === 'Renaksi' ? 'lp-renaksi' :
                          selectedIgtDetail.kelompok_igt === 'Walidata' ? 'lp-walidata' : 'lp-both'
                        }`}>
                        {selectedIgtDetail.kelompok_igt || '-'}
                      </span>
                    </div>
                    <div className="lp-modal-info-item">
                      <span className="lp-modal-label">Jenis KSP</span>
                      <span className="lp-modal-value">{selectedIgtDetail.jenis_igt || '-'}</span>
                    </div>
                    {selectedIgtDetail.jenis_igt === 'Renaksi PKSP' && (
                      <div className="lp-modal-info-item">
                        <span className="lp-modal-label">Status Publikasi</span>
                        <span className="lp-modal-value">{selectedIgtDetail.status_publikasi || '-'}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Capaian & Target - Only for Renaksi PKSP */}
                {selectedIgtDetail.jenis_igt === 'Renaksi PKSP' && (
                  <>
                    {/* Capaian Section */}
                    <div className="lp-modal-section">
                      <h4>Capaian Renaksi</h4>
                      {(() => {
                        let capaianList = [];
                        if (selectedIgtDetail.capaian_data) {
                          try {
                            capaianList = typeof selectedIgtDetail.capaian_data === 'string'
                              ? JSON.parse(selectedIgtDetail.capaian_data)
                              : selectedIgtDetail.capaian_data;
                          } catch (e) {
                            console.error('Error parsing capaian_data:', e);
                          }
                        }

                        if (!Array.isArray(capaianList) || capaianList.length === 0) {
                          return <p className="lp-modal-empty">Belum ada data capaian</p>;
                        }

                        return (
                          <div className="lp-capaian-cards">
                            {capaianList.sort((a, b) => (a.tahun || 0) - (b.tahun || 0)).map((item, idx) => (
                              <div key={idx} className="lp-capaian-card">
                                <div className="lp-capaian-year">{item.tahun}</div>
                                <div className="lp-capaian-persentase">
                                  <span className={`lp-persentase-badge ${item.persentase >= 100 ? 'lp-full' : item.persentase >= 50 ? 'lp-half' : 'lp-low'}`}>
                                    {item.persentase || 0}%
                                  </span>
                                </div>
                                <div className="lp-capaian-value">{item.nilai} {item.satuan || ''}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Target Section */}
                    <div className="lp-modal-section">
                      <h4>Target Renaksi</h4>
                      {(() => {
                        let targetObj = {};
                        if (selectedIgtDetail.target) {
                          try {
                            targetObj = typeof selectedIgtDetail.target === 'string'
                              ? JSON.parse(selectedIgtDetail.target)
                              : selectedIgtDetail.target;
                          } catch (e) {
                            console.error('Error parsing target:', e);
                          }
                        }

                        const targetEntries = Object.entries(targetObj).sort(([a], [b]) => parseInt(a) - parseInt(b));

                        if (targetEntries.length === 0) {
                          return <p className="lp-modal-empty">Belum ada data target</p>;
                        }

                        return (
                          <div className="lp-target-cards">
                            {targetEntries.map(([year, value], idx) => (
                              <div key={idx} className="lp-target-card">
                                <div className="lp-target-year">{year}</div>
                                <div className="lp-target-value">{value} {selectedIgtDetail.satuan || ''}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}

                {/* Document Completeness */}
                <div className="lp-modal-section">
                  <h4>Kelengkapan Dokumen</h4>
                  <div className="lp-modal-doc-grid">
                    {(() => {
                      const rawStatus = selectedIgtDetail.raw_status;
                      const statusDisplay = selectedIgtDetail.status_display;

                      const getDocStatus = (has, dbStatus) => {
                        if (statusDisplay === 'Sudah Terintegrasi') {
                          if (dbStatus === 'Sesuai' || dbStatus === 'Tidak Sesuai') {
                            return { label: dbStatus, class: dbStatus === 'Sesuai' ? 'lp-doc-sesuai' : 'lp-doc-tidak-sesuai' };
                          }
                          return { label: 'Sesuai', class: 'lp-doc-sesuai' }; // Default to Sesuai if integrated
                        }

                        if (statusDisplay === 'Proses Verifikasi') {
                          if (rawStatus === 'Belum Diverifikasi') {
                            return { label: 'Belum Diperiksa', class: 'lp-doc-pending' };
                          }

                          if (dbStatus === 'Sesuai' || dbStatus === 'Tidak Sesuai') {
                            return { label: dbStatus, class: dbStatus === 'Sesuai' ? 'lp-doc-sesuai' : 'lp-doc-tidak-sesuai' };
                          }
                          return { label: 'Belum Diperiksa', class: 'lp-doc-pending' };
                        }

                        // Belum Pengajuan
                        if (!has) {
                          return { label: 'Belum Diupload', class: 'lp-doc-empty' };
                        }
                        return { label: 'Belum Diperiksa', class: 'lp-doc-pending' };
                      };

                      const docList = [
                        { name: 'Peta', key: 'peta', has: selectedIgtDetail.has_peta, status: selectedIgtDetail.status_peta },
                        { name: 'Struktur Data', key: 'struktur_data', has: selectedIgtDetail.has_struktur_data, status: selectedIgtDetail.status_struktur_data },
                        { name: 'Mekanisme dan Tata Kerja', key: 'takel', has: selectedIgtDetail.has_takel, status: selectedIgtDetail.status_takel },
                        { name: 'Dokumen Penetapan', key: 'dokumen', has: selectedIgtDetail.has_dokumen, status: selectedIgtDetail.status_dokumen },
                        { name: 'Metadata', key: 'metadata', has: selectedIgtDetail.has_metadata, status: selectedIgtDetail.status_metadata },
                        { name: 'Simbologi', key: 'simbology', has: selectedIgtDetail.has_simbology, status: selectedIgtDetail.status_simbology },
                        { name: 'Role Akses', key: 'role', has: selectedIgtDetail.has_role, status: selectedIgtDetail.status_role },
                        { name: 'Kontrol Kualitas', key: 'kualitas', has: selectedIgtDetail.has_kualitas, status: selectedIgtDetail.status_kualitas },
                        { name: 'Cakupan', key: 'cakupan', has: selectedIgtDetail.has_cakupan, status: selectedIgtDetail.status_cakupan }
                      ];

                      return docList.map((doc, idx) => {
                        const checklistItems = selectedIgtDetail.checklist_items?.[doc.key] || [];
                        const docStatus = getDocStatus(doc.has, doc.status);
                        const isExpanded = expandedChecklists[doc.key] || false;

                        return (
                          <div key={idx} className="lp-modal-doc-item">
                            <div className="lp-modal-doc-header">
                              <span className="lp-modal-doc-name">{doc.name}</span>
                              <div className="lp-modal-doc-actions">
                                <span className={`lp-doc-badge ${docStatus.class}`}>
                                  {docStatus.label}
                                </span>
                                {checklistItems.length > 0 && (
                                  <button
                                    className="lp-checklist-toggle"
                                    onClick={() => setExpandedChecklists(prev => ({
                                      ...prev,
                                      [doc.key]: !prev[doc.key]
                                    }))}
                                    title={isExpanded ? 'Tutup Checklist' : 'Lihat Checklist'}
                                  >
                                    <FaClipboardList />
                                    <span className="lp-checklist-count">{checklistItems.length}</span>
                                    {isExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                  </button>
                                )}
                              </div>
                            </div>
                            {/* Show checklist items if expanded */}
                            {isExpanded && checklistItems.length > 0 && (
                              <ul className="lp-modal-checklist">
                                {checklistItems.map((item, itemIdx) => (
                                  <li key={itemIdx} className="lp-modal-checklist-item">
                                    <span className="lp-checklist-icon">✓</span>
                                    <span className="lp-checklist-text">{item.description}</span>
                                    {item.notes && (
                                      <span className="lp-checklist-notes">{item.notes}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Features Section */}
      {/* <section className="lp-features-section">
        <motion.div 
          className="lp-features-container"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="lp-section-title">Fitur Utama</h2>
          
          <div className="lp-features-grid">
            <motion.div 
              className="lp-feature-card"
              whileHover={{ y: -10 }}
            >
              <div className="lp-feature-icon">📤</div>
              <h3>Upload Data</h3>
              <p>Upload data IGT dengan metadata lengkap dalam berbagai format geospasial</p>
            </motion.div>
            
            <motion.div 
              className="lp-feature-card"
              whileHover={{ y: -10 }}
            >
              <div className="lp-feature-icon">✅</div>
              <h3>Verifikasi</h3>
              <p>Proses verifikasi data oleh tim verifikator yang berpengalaman</p>
            </motion.div>
            
            <motion.div 
              className="lp-feature-card"
              whileHover={{ y: -10 }}
            >
              <div className="lp-feature-icon">📊</div>
              <h3>Monitoring</h3>
              <p>Pantau progress dan status data secara real-time melalui dashboard</p>
            </motion.div>
            
            <motion.div 
              className="lp-feature-card"
              whileHover={{ y: -10 }}
            >
              <div className="lp-feature-icon">🔗</div>
              <h3>Integrasi</h3>
              <p>Integrasi data ke dalam sistem Kebijakan Satu Peta</p>
            </motion.div>
          </div>
        </motion.div>
      </section> */}

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-content">
          <div className="lp-footer-brand">
            <img src="/logo.png" alt="KSP Logo" />
            <p>Aplikasi Integrasi IGT</p>
          </div>
          <div className="lp-footer-links">
            <h4>Website terkait</h4>
            <a href="https://www.big.go.id/" target="_blank" rel="noopener noreferrer">Badan Informasi Geospasial</a>
            <a href="https://onemap.big.go.id" target="_blank" rel="noopener noreferrer">Kebijakan Satu Peta</a>
            <a href="https://renaksi-ignas.big.go.id/" target="_blank" rel="noopener noreferrer">Renaksi IGNAS</a>
            <a href="https://simpatig.big.go.id/" target="_blank" rel="noopener noreferrer">Simpatig</a>
          </div>
          <div className="lp-footer-contact">
            <h4>Kontak</h4>
            <p>Badan Informasi Geospasial</p>
            <p>Jl. Raya Jakarta - Bogor Km 46</p>
            <p>Cibinong, Bogor 16911</p>
            <p>Email: info.disigt@big.go.id</p>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <p>© {new Date().getFullYear()} BIG - Badan Informasi Geospasial. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;